import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { toast } from 'react-toastify';
import { ComplaintIDBadge, EmployeeIDBadge } from '../../../shared/components/common/Badges';
import { formatPhoneNumber, removeCountryCode } from '../../../utils/phoneFormatter';
import { formatRoleName } from '../../../utils/roleFormatter';
import './ComplaintConfirmation.css';

function DetailRow({ label, children }) {
  if (!children) return null;

  return (
    <div className="auth-confirmation-detail-row">
      <span className="auth-confirmation-detail-label">{label}</span>
      <span className="auth-confirmation-detail-text">{children}</span>
    </div>
  );
}

function ComplaintConfirmation({ confirmation, user, onClose, onViewComplaint }) {
  const [copyFeedback, setCopyFeedback] = useState(false);
  const isCentralHelpDesk = user?.role === 'central_help_desk';
  const complaintId = confirmation?.complaint_identifier || confirmation?.id || 'N/A';
  const complaintDatabaseId = confirmation?.id != null ? confirmation.id : null;
  const canOpenComplaintView =
    typeof onViewComplaint === 'function' &&
    complaintDatabaseId != null &&
    complaintDatabaseId !== '' &&
    complaintDatabaseId !== 'N/A';
  const reporter = confirmation?.reporter || {};
  const assignedTechnician = confirmation?.assignedTechnician || {};
  const attachedPhotos = confirmation?.attachedPhotos || [];
  const hasAttachedPhotos = attachedPhotos.length > 0;

  useEffect(() => {
    const scrollY = window.scrollY;
    const originalBodyStyles = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width
    };

    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    return () => {
      document.body.style.overflow = originalBodyStyles.overflow;
      document.body.style.position = originalBodyStyles.position;
      document.body.style.top = originalBodyStyles.top;
      document.body.style.width = originalBodyStyles.width;
      window.scrollTo(0, scrollY);
    };
  }, []);

  const formatContact = (contactNumber) => {
    if (!contactNumber) return null;
    return formatPhoneNumber(removeCountryCode(contactNumber));
  };

  const handleViewComplaint = () => {
    if (!canOpenComplaintView) return;
    onViewComplaint(complaintDatabaseId);
  };

  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(complaintId || '');
      setCopyFeedback(true);
      toast.success('Complaint ID copied to clipboard!');
      setTimeout(() => setCopyFeedback(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy ID to clipboard');
    }
  };

  return ReactDOM.createPortal(
    <div className="modal-overlay auth-confirmation-overlay">
      <div className="auth-confirmation-card" onClick={(e) => e.stopPropagation()}>
        <button className="auth-confirmation-close" onClick={onClose} aria-label="Close confirmation">
          ✕
        </button>

        <div className="auth-confirmation-hero auth-confirmation-hero--with-photos">
          <div className="auth-confirmation-success-section">
            <div className="auth-confirmation-success-icon">✓</div>
            <div className="auth-confirmation-success-copy">
              <h1 className="auth-confirmation-title">
                {isCentralHelpDesk ? 'Complaint Logged on Behalf of Reporter' : 'Your Complaint Has Been Logged'}
              </h1>
              <p className="auth-confirmation-subtitle">
                {isCentralHelpDesk
                  ? 'Share this complaint ID with the reporter so they can reference it in follow-up calls.'
                  : 'Our team will investigate and resolve your issue.'}
              </p>
              {confirmation?.photoUploadWarning && (
                <p className="auth-confirmation-subtitle auth-confirmation-photo-warning">
                  {confirmation.photoUploadWarning}
                </p>
              )}
            </div>
          </div>

          <div
            className={`auth-confirmation-photos${hasAttachedPhotos ? '' : ' auth-confirmation-photos--empty'}`}
            aria-label={hasAttachedPhotos ? 'Attached photos' : 'No photos attached'}
          >
            <span className="auth-confirmation-photos-label">
              {hasAttachedPhotos
                ? `${attachedPhotos.length} photo${attachedPhotos.length !== 1 ? 's' : ''} attached`
                : 'No photos attached'}
            </span>
            {hasAttachedPhotos ? (
              <div className="auth-confirmation-photos-strip">
                {attachedPhotos.map((photo, index) => (
                  <img
                    key={`${photo.name || 'photo'}-${index}`}
                    src={photo.previewUrl}
                    alt={photo.name || `Attached photo ${index + 1}`}
                    className="auth-confirmation-photo-thumb"
                    title={photo.name || `Photo ${index + 1}`}
                  />
                ))}
              </div>
            ) : null}
          </div>

          <div className="auth-confirmation-id-section">
            <span className="auth-confirmation-id-label">Complaint ID:</span>
            <div className="auth-confirmation-id-display">
              <ComplaintIDBadge complaintId={complaintId} size="large" />
              <button
                className={`auth-confirmation-copy-btn ${copyFeedback ? 'copied' : ''}`}
                onClick={handleCopyId}
                title="Copy to clipboard"
                aria-label="Copy complaint ID"
              >
                {copyFeedback ? '✓' : '📋'}
              </button>
            </div>
            <p className="auth-confirmation-id-hint">Save this number to track the complaint</p>
          </div>
        </div>

        <div className="auth-confirmation-details-grid">
          <div className="auth-confirmation-info-card">
            <div className="auth-confirmation-card-header">
              <span className="auth-confirmation-card-icon">👤</span>
              <span className="auth-confirmation-card-title">
                {isCentralHelpDesk ? 'Reporter Details' : 'Reported By'}
              </span>
            </div>
            <div className="auth-confirmation-card-content">
              <DetailRow label="Name:">{reporter.full_name || reporter.employee_id}</DetailRow>
              {reporter.employee_id && (
                <DetailRow label="Employee:">
                  <EmployeeIDBadge employeeId={reporter.employee_id} size="small" />
                </DetailRow>
              )}
              <DetailRow label="Email:">{reporter.email}</DetailRow>
              <DetailRow label="Contact:">{formatContact(reporter.contact_number)}</DetailRow>
              <DetailRow label="Role:">{reporter.role ? formatRoleName(reporter.role) : null}</DetailRow>
            </div>
          </div>

          <div className="auth-confirmation-info-card auth-confirmation-assigned-card">
            <div className="auth-confirmation-card-header">
              <span className="auth-confirmation-card-icon">⚙️</span>
              <span className="auth-confirmation-card-title">Assigned Technician</span>
            </div>
            <div className="auth-confirmation-card-content">
              {assignedTechnician.employee_id ? (
                <>
                  <DetailRow label="Name:">{assignedTechnician.full_name || 'Technician'}</DetailRow>
                  <DetailRow label="Employee:">
                    <EmployeeIDBadge employeeId={assignedTechnician.employee_id} size="small" />
                  </DetailRow>
                  <DetailRow label="Email:">{assignedTechnician.email}</DetailRow>
                  <DetailRow label="Contact:">{formatContact(assignedTechnician.contact_number)}</DetailRow>
                  <DetailRow label="Role:">
                    {assignedTechnician.role ? formatRoleName(assignedTechnician.role) : null}
                  </DetailRow>
                </>
              ) : (
                <DetailRow label="Status:">Pending assignment</DetailRow>
              )}
            </div>
          </div>
        </div>

        <div className="auth-confirmation-footer-row">
          <div className="auth-confirmation-next-steps">
            <div className="auth-confirmation-next-title">What Happens Next?</div>
            <div className="auth-confirmation-next-item">
              <span className="auth-confirmation-next-icon">⏱️</span>
              <span>Our team will respond within the <strong>defined TAT</strong> for this location and severity</span>
            </div>
            <div className="auth-confirmation-next-item">
              <span className="auth-confirmation-next-icon">📞</span>
              <span>
                {isCentralHelpDesk
                  ? <>The reporter may be contacted for <strong>additional details</strong></>
                  : <>We may call you for <strong>additional details</strong></>}
              </span>
            </div>
          </div>

          <div className="auth-confirmation-actions">
            {canOpenComplaintView && (
              <button type="button" className="action-btn action-secondary" onClick={handleViewComplaint}>
                <span className="action-icon" aria-hidden="true">
                  →
                </span>
                <span className="action-text">View complaint</span>
              </button>
            )}
            <button type="button" className="action-btn action-primary" onClick={onClose}>
              <span className="action-icon">✓</span>
              <span className="action-text">Done</span>
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ComplaintConfirmation;
